import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery as useApolloQuery } from '@apollo/client/react';
import { GET_FLOWS } from '../../../lib/graphql/queries/organizations';
import { useOrganizationTimezone } from '../../../stores/useOrganizationStore';
import { formatDate } from '../../../utils/dateTime';
import type { Flow, ConvertFlow, FlowType } from './useFlowsTypes';

// GQL FlowSummary shape returned by the backend
interface GQLFlowSummary {
  organizationId: string;
  flowId: string;
  name: string;
  description: string | null;
  flowType: string | null;
  subType: string | null;
  status: string | null;
  scheduledFor: string | null;
  timezone: string | null;
  allowedHours: string | null;    // JSON string: { start_hour, end_hour }
  allowedDays: string | null;     // JSON string: string[]
  excludeHolidays: boolean | null;
  countryCode: string | null;
  scheduleOverUnit: string | null;
  schedulingWindow: number | null;
  promptParameters: string | null; // JSON string
  descriptionId: string | null;
  transferNumbers: string | null;  // JSON string: Record<string, string>
  useRoundRobin: boolean | null;
  activeCount: number | null;
  leadSource: string | null;
  leadEmail: string | null;
  voicemailContent: string | null;
  createdAt: string | null;
  deletedAt: string | null;
  contactCount: number | null;
  callCount: number | null;
}

interface GetFlowsData {
  flows: {
    flows: GQLFlowSummary[];
    total: number;
    hasMore: boolean;
  };
}

function tryParseJSON<T>(value: string | null | undefined, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

function mapGQLFlow(f: GQLFlowSummary, timezone: string): Flow {
  return {
    id: f.flowId,
    name: f.name ?? '',
    status: (f.status ?? '').toLowerCase(),
    type: (f.flowType ?? '') as FlowType,
    sub_type: f.subType ?? '',
    // Format scheduled_for for display (same as useFlowsQuery)
    scheduled_for: f.scheduledFor ? formatDate(f.scheduledFor, timezone) : '',
    scheduled_for_raw: f.scheduledFor ?? '',
    contacts_count: f.contactCount ?? f.activeCount ?? 0,
    calls_count: f.callCount ?? 0,
    created_at: f.createdAt ?? '',
    description: f.description ?? '',
    description_id: f.descriptionId ?? '',
    timezone: f.timezone ?? '',
    country_code: f.countryCode ?? '',
    scheduling_window: f.schedulingWindow ?? 0,
    schedule_over_unit: f.scheduleOverUnit ?? '',
    allowed_days: tryParseJSON<string[]>(f.allowedDays, []),
    allowed_hours: tryParseJSON<{ start_hour: number; end_hour: number }>(
      f.allowedHours,
      { start_hour: 9, end_hour: 18 }
    ),
    exclude_holidays: f.excludeHolidays ?? false,
    useRoundRobin: f.useRoundRobin ?? false,
    voicemailContent: f.voicemailContent ?? '',
    transfer_numbers: tryParseJSON<Record<string, string>>(f.transferNumbers, {}),
    prompt_parameters: f.promptParameters
      ? tryParseJSON<Flow['prompt_parameters']>(f.promptParameters, undefined)
      : undefined,
  } as Flow;
}

function mapGQLConvertFlow(f: GQLFlowSummary, timezone: string): ConvertFlow {
  return {
    ...mapGQLFlow(f, timezone),
    lead_source: f.leadSource ?? '',
    lead_email: f.leadEmail ?? '',
  };
}

interface UseFlowsGQLProps {
  flowType: FlowType;
  organizationId: string;
  pageSize?: number;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  searchQuery?: string;
  filters?: {
    status?: string[];
    subType?: string[];
    dateRange?: {
      startDate: Date | null;
      endDate: Date | null;
    };
  };
}

const DEFAULT_PAGE_SIZE = 25;
// When dateRange filter is active (no server support), fetch up to this many flows client-side.

// UI snake_case sort field → API camelCase sortBy value
const SORT_KEY_MAP: Record<string, string> = {
  name: 'name',
  status: 'status',
  sub_type: 'subType',
  subType: 'subType',
  scheduled_for: 'scheduledFor',
  scheduledFor: 'scheduledFor',
  lead_source: 'leadSource',
  leadSource: 'leadSource',
  lead_email: 'leadEmail',
  leadEmail: 'leadEmail',
  created_at: 'createdAt',
  createdAt: 'createdAt',
};

/**
 * GQL-backed replacement for useFlowsQuery.
 *
 * Uses the top-level `flows` query with server-side filtering by flowType,
 * search, sortBy, and sortOrder. Multi-value filters (status[], subType[])
 * and dateRange are applied client-side since the API accepts single values only.
 *
 * Returns the same shape as useFlowsQuery so pages require minimal changes.
 */
export const useFlowsGQL = ({
  flowType,
  organizationId,
  pageSize = DEFAULT_PAGE_SIZE,
  sortField,
  sortDirection = 'desc',
  searchQuery,
  filters,
}: UseFlowsGQLProps) => {
  const timezone = useOrganizationTimezone();
  const [currentPage, setCurrentPage] = useState(0);

  const apiSortBy = sortField ? SORT_KEY_MAP[sortField] : undefined;

  // Send single-value filters server-side; backend only accepts String (not array)
  const serverStatus = filters?.status?.length === 1 ? filters.status[0] : undefined;
  const serverSubType = filters?.subType?.length === 1 ? filters.subType[0] : undefined;
  const scheduledAfter = filters?.dateRange?.startDate?.toISOString() ?? undefined;
  const scheduledBefore = filters?.dateRange?.endDate?.toISOString() ?? undefined;

  const { data, loading, error, refetch: apolloRefetch } = useApolloQuery<GetFlowsData>(
    GET_FLOWS,
    {
      variables: {
        organizationId,
        limit: pageSize,
        offset: currentPage * pageSize,
        flowType,
        status: serverStatus,
        subType: serverSubType,
        search: searchQuery?.trim() || undefined,
        sortBy: apiSortBy,
        sortOrder: sortDirection.toUpperCase(),
        scheduledAfter,
        scheduledBefore,
      },
      skip: !organizationId,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    }
  );

  // Reset to page 0 when search, filters, or sort changes
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);
  useEffect(() => {
    setCurrentPage(0);
  }, [searchQuery, filtersKey, sortField, sortDirection]);

  const filteredRaw = useMemo(
    () => (data?.flows?.flows ?? []).filter((f) => !f.deletedAt),
    [data]
  );

  const totalCount = data?.flows?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const flows = useMemo(() => {
    if (flowType === 'Convert') return filteredRaw.map((f) => mapGQLConvertFlow(f, timezone));
    return filteredRaw.map((f) => mapGQLFlow(f, timezone));
  }, [filteredRaw, flowType, timezone]);

  const onNextPage = useCallback(() => {
    setCurrentPage((p) => Math.min(p + 1, totalPages - 1));
  }, [totalPages]);

  const onPrevPage = useCallback(() => {
    setCurrentPage((p) => Math.max(p - 1, 0));
  }, []);

  const onPageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const refetch = useCallback(async () => {
    setCurrentPage(0);
    const result = await apolloRefetch();
    const fresh = (result?.data?.flows?.flows ?? []).filter((f) => !f.deletedAt);
    if (flowType === 'Convert') {
      return fresh.map((f) => mapGQLConvertFlow(f, timezone)) as Flow[] | ConvertFlow[];
    }
    return fresh.map((f) => mapGQLFlow(f, timezone)) as Flow[];
  }, [apolloRefetch, flowType, timezone]);

  const pagesData = useMemo(
    () => ({ pages: [{ flows, totalCount, cursor: undefined }] }),
    [flows, totalCount]
  );

  return {
    data: pagesData,
    flows,
    totalCount,
    currentPage,
    totalPages,
    onNextPage,
    onPrevPage,
    onPageChange,
    isLoading: loading,
    error: error ?? null,
    refetch,
    isFetchingNextPage: false,
  };
};
